import { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash, ShoppingBag, ArrowLeft, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Cart() {
  const { items, setQty, remove, total } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div className="pt-24 min-h-screen">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
        <Link to="/shop" className="inline-flex items-center gap-1 text-sm font-medium text-[#63675E] hover:text-[#2E523A] mb-6 transition-colors duration-200" data-testid="back-to-shop">
          <ArrowLeft size={16} /> Continue shopping
        </Link>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mb-8">Your cart</h1>

        {items.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-[#D2D5CD] rounded-2xl" data-testid="empty-cart">
            <ShoppingBag size={48} weight="duotone" className="text-[#4A6E7C] mx-auto mb-4" />
            <p className="text-[#63675E] mb-4">Your cart is empty.</p>
            <Link to="/shop"><Button className="rounded-full bg-[#2E523A] hover:bg-[#26442f]">Browse the shop</Button></Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
              {items.map((i) => (
                <div key={i.lookup_key} className="flex gap-4 border border-[#D2D5CD] rounded-2xl p-4 bg-white" data-testid={`cart-item-${i.lookup_key}`}>
                  <img src={i.image} alt={i.name} className="h-24 w-24 rounded-xl object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-lg font-bold">{i.name}</h3>
                    <p className="text-sm text-[#63675E]">{i.category}</p>
                    <div className="flex items-center gap-3 mt-3">
                      <div className="flex items-center border border-[#D2D5CD] rounded-full">
                        <button onClick={() => setQty(i.lookup_key, i.quantity - 1)} className="h-9 w-9 flex items-center justify-center hover:text-[#2E523A]" data-testid={`decrease-${i.lookup_key}`}><Minus size={16} /></button>
                        <span className="w-8 text-center font-semibold" data-testid={`qty-${i.lookup_key}`}>{i.quantity}</span>
                        <button onClick={() => setQty(i.lookup_key, i.quantity + 1)} className="h-9 w-9 flex items-center justify-center hover:text-[#2E523A]" data-testid={`increase-${i.lookup_key}`}><Plus size={16} /></button>
                      </div>
                      <button onClick={() => remove(i.lookup_key)} className="text-[#63675E] hover:text-destructive transition-colors duration-200 flex items-center gap-1 text-sm" data-testid={`remove-${i.lookup_key}`}>
                        <Trash size={16} /> Remove
                      </button>
                    </div>
                  </div>
                  <div className="font-display text-xl font-bold text-[#2E523A] shrink-0">${(i.price * i.quantity).toFixed(2)}</div>
                </div>
              ))}
            </div>

            <div className="lg:col-span-1">
              <div className="border border-[#D2D5CD] rounded-2xl p-6 bg-white sticky top-24">
                <h3 className="font-display text-xl font-bold mb-4">Order summary</h3>
                <div className="flex justify-between text-[#63675E] mb-2">
                  <span>Subtotal</span>
                  <span data-testid="cart-subtotal">${total.toFixed(2)}</span>
                </div>
                <p className="text-xs text-[#63675E] mb-4">Taxes calculated at checkout.</p>
                <Button onClick={checkout} disabled={loading} className="w-full rounded-full bg-[#2E523A] hover:bg-[#26442f] h-12 text-base" data-testid="checkout-btn">
                  {loading ? "Redirecting…" : "Checkout"}
                </Button>
                <p className="flex items-center justify-center gap-1.5 text-xs text-[#63675E] mt-4">
                  <ShieldCheck size={15} weight="duotone" /> Secure payment via Stripe
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
